import { useEffect, useRef } from "react";
import { resizeIFrame } from "./resizeIFrame";
import hasIntersectionObserver from "./hasIntersectionObserver";

let moduleObserver: IntersectionObserver;
const getObserver = () => {
  if (!hasIntersectionObserver()) {
    return;
  }
  if (!moduleObserver)
    moduleObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // indicate to TS this is not just any Element to access dataset, src, and srcset properties
            const mediaEl = entry.target as
              | HTMLImageElement
              | HTMLIFrameElement;
            // 1. assign data-src to the src attribute
            if (mediaEl.dataset.src) mediaEl.src = mediaEl.dataset.src;
            // 2. set images srcset attribute, if srcset exists
            if (mediaEl instanceof HTMLImageElement && mediaEl.dataset.srcset)
              mediaEl.srcset = mediaEl.dataset.srcset;
            // 3. resize iframes
            if (mediaEl instanceof HTMLIFrameElement) resizeIFrame(mediaEl);
            // follow current ATL conventions of updating the lazyload class
            mediaEl.classList.remove("lazyload");
            mediaEl.classList.add("lazyloaded");
            moduleObserver.unobserve(mediaEl);
          }
        });
      },
      { rootMargin: "200px", threshold: 0 }
    );
  return moduleObserver;
};

const useImperativeLazyLoad = () => {
  const ref = useRef<HTMLDivElement>(null!);
  useEffect(() => {
    // lazyload mediaEls
    const mediaEls = ref.current?.querySelectorAll(
      "img.lazyload, iframe.lazyload"
    );
    // check if intersection observer already exists
    // if not, make a new one otherwise use the existing one
    const observer = getObserver();
    if (observer) {
      mediaEls?.forEach((mediaEl) => observer?.observe(mediaEl));
      return () => {
        mediaEls?.forEach((mediaEl) => observer?.unobserve(mediaEl));
      };
    } else {
      mediaEls?.forEach((mediaEl: HTMLImageElement | HTMLIFrameElement) => {
        // 1. assign data-src to the src attribute
        if (mediaEl.dataset.src) mediaEl.src = mediaEl.dataset.src;
        // 2. set images srcset attribute, if srcset exists
        if (mediaEl instanceof HTMLImageElement && mediaEl.dataset.srcset)
          mediaEl.srcset = mediaEl.dataset.srcset;
        // 3. resize iframes
        if (mediaEl instanceof HTMLIFrameElement) resizeIFrame(mediaEl);
        // follow current ATL conventions of updating the lazyload class
        mediaEl.classList.remove("lazyload");
        mediaEl.classList.add("lazyloaded");
      });
    }
  }, []);
  return ref;
};

export default useImperativeLazyLoad;
